import React, { useCallback, useState } from 'react';

import { apiDeleteAsUser } from '../../../lib/api_v2';
import { assignDocument } from '../../../lib/desktop_navigation_diagnostics';
import { logError } from '../../../lib/logger';
import { rootDomainUrl } from '../../../lib/meta';
import { cn } from '../../../lib/utils';

import { FIELD_INPUT_CLASS, FIELD_INPUT_ERROR_CLASS, FIELD_LABEL_CLASS, firstFieldError, responseValidations } from './accountStyles';

// Canonical pills: destructive = MembersPanel's red-outline Remove pill;
// secondary = the gray pill used across the settings modals.
const DANGER_BUTTON_CLASS =
  'flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-full border border-red-500 px-3 text-cursed text-red-500 hover:bg-red-500/5 disabled:cursor-not-allowed disabled:opacity-50';
const SECONDARY_BUTTON_CLASS =
  'flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-full border-[0.5px] border-white bg-gray-200 px-3 text-cursed text-black hover:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-50 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700';

const AccountAdvancedPanel = () => {
  const [isConfirming, setIsConfirming] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [errors, setErrors] = useState({});
  const panelError = firstFieldError(errors.general || errors.base);
  const currentPasswordError = firstFieldError(errors.current_password);
  const isDeleteDisabled = isDeleting || currentPassword.length === 0;

  const handleRequestConfirm = useCallback(function requestAccountDeleteConfirm() {
    setErrors({});
    setIsConfirming(true);
  }, []);

  const handleCancelConfirm = useCallback(function cancelAccountDeleteConfirm() {
    setCurrentPassword('');
    setErrors({});
    setIsConfirming(false);
  }, []);

  const handleCurrentPasswordChange = useCallback(function handleAccountDeleteCurrentPasswordChange(event) {
    setCurrentPassword(event.target.value);
    setErrors((prev) => ({ ...prev, current_password: null, general: null, base: null }));
  }, []);

  const handleConfirmDelete = useCallback(
    async function confirmAccountDelete(event) {
      event.preventDefault();
      if (isDeleteDisabled) return;

      setIsDeleting(true);
      setErrors({});

      try {
        const response = await apiDeleteAsUser('/account', {
          user: { current_password: currentPassword },
        });
        const data = await response.json();

        if (!response.ok || data.ok !== true) {
          setErrors(responseValidations(data) || { general: 'Unable to delete your account. Please try again.' });
          setIsDeleting(false);
          return;
        }

        assignDocument(rootDomainUrl('/'), 'logout');
      } catch (deleteError) {
        logError('AccountAdvancedPanel', 'Failed to delete account', { error: deleteError });
        setErrors({ general: 'Unable to delete your account. Please try again.' });
        setIsDeleting(false);
      }
    },
    [currentPassword, isDeleteDisabled],
  );

  return (
    <div className="flex w-full flex-col gap-6 pt-6">
      <div className="flex flex-col gap-2 text-cursed text-gray-500 dark:text-neutral-400">
        <p className="font-semibold text-black dark:text-neutral-100">Deleting your account is permanent and cannot be undone.</p>
        <p>You'll be removed from all spaces you're a member of, but your messages in those spaces will remain.</p>
        <p>Please be certain before proceeding. You cannot undo this.</p>
      </div>

      {panelError && <div className="rounded-md bg-red-50 p-4 text-sm text-red-800 dark:bg-red-950 dark:text-red-200">{panelError}</div>}

      {isConfirming ? (
        <form
          onSubmit={handleConfirmDelete}
          className="flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-5 dark:border-red-500/30 dark:bg-red-950/40"
        >
          <p className="text-cursed font-medium text-red-700 dark:text-red-200">Are you sure? This cannot be undone.</p>
          <div className="flex flex-col gap-2">
            <label htmlFor="account_delete_current_password" className={FIELD_LABEL_CLASS}>
              Current password
            </label>
            <input
              id="account_delete_current_password"
              name="current_password"
              type="password"
              autoComplete="current-password"
              value={currentPassword}
              onChange={handleCurrentPasswordChange}
              placeholder="Current password"
              className={cn(FIELD_INPUT_CLASS, currentPasswordError && FIELD_INPUT_ERROR_CLASS)}
            />
            {currentPasswordError && <span className="text-xs text-red-400">{currentPasswordError}</span>}
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" data-app-region="none" disabled={isDeleteDisabled} className={DANGER_BUTTON_CLASS}>
              {isDeleting ? 'Deleting…' : 'Confirm delete'}
            </button>
            <button
              type="button"
              data-app-region="none"
              onClick={handleCancelConfirm}
              disabled={isDeleting}
              className={SECONDARY_BUTTON_CLASS}
            >
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <button type="button" data-app-region="none" onClick={handleRequestConfirm} className={cn(DANGER_BUTTON_CLASS, 'self-start')}>
          Delete my account
        </button>
      )}
    </div>
  );
};

export default AccountAdvancedPanel;
